import React from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  confirmTransfer,
  newTransferRequestKey,
  setTransferEventLink,
  setTransferStopProgress,
  type TransferRecord,
  type TransferStopProgress,
} from '@/data/transferRecord';
import { R2_TRANSFER_REASON, buildPackingListHtml, describeStatus, pluralize } from '@/domain/warehouse';
import AutosaveStatus from '../../components/AutosaveStatus';
import ErrorBanner from '../../components/ErrorBanner';
import LifecycleStageTracker from '../../components/LifecycleStageTracker';
import RecordHeader from '../../components/RecordHeader';
import RecordMeltBar from '../../components/records/RecordMeltBar';
import {
  RecordMobileIdentity,
  RecordNextStep,
  RecordOverviewLayout,
  RecordPhoneActionBar,
  RecordSectionTabs,
  type RecordSectionTab,
} from '../../components/records/RecordLayout';
import { LateDate, R2ManagedBanner, SourceTag } from '../../components/status/StatusChip';
import EventLinkSelector from '../../components/transfers/EventLinkSelector';
import useMediaQuery from '../../hooks/useMediaQuery';
import { openPrintableDocument } from '../../lib/printDocument';
import useSessionStore from '../../state/session';
import { MeltBarSlotProvider } from '../../ui/MeltBarSlotContext';
import { usePageTitleOverride } from '../../ui/pageTitle';
import { useToasts } from '../../ui/ToastProvider';
import { useHeaderMelt } from '../../ui/useHeaderMelt';
import { readableErrorMessage } from '../../utils/appErrors';
import TransferActionButton from './record/TransferActionButton';
import TransferChatSection from './record/TransferChatSection';
import TransferEventsSection from './record/TransferEventsSection';
import TransferHistorySection from './record/TransferHistorySection';
import {
  TransferCancelDialog,
  TransferDatesDialog,
  TransferReturnLegDialog,
  TransferShipDialog,
} from './record/TransferLifecycleDialogs';
import TransferLineDialog from './record/TransferLineDialog';
import TransferLinesSection from './record/TransferLinesSection';
import TransferMoreMenu, { type TransferMenuItem } from './record/TransferMoreMenu';
import TransferRouteSection from './record/TransferRouteSection';
import TransferScanDialog, { type TransferScanMode } from './record/TransferScanDialog';
import TransferShipmentDialog from './record/TransferShipmentDialog';
import TransferStopDialog from './record/TransferStopDialog';
import TransferSummaryRail from './record/TransferSummaryRail';
import {
  describeTransferActions,
  eventLabel,
  formatSiteDateTime,
  formatSiteDayWord,
  formatSiteMoment,
  isR2Record,
  logisticsSummary,
  nextStepFor,
  packingListInputFor,
  primaryActionFor,
  resolveTransferSection,
  scopeLabel,
  shipShortFor,
  shortSiteName,
  siteName,
  trackerFor,
  type TransferActionViews,
  type TransferSection,
} from './record/transferRecordView';
import { useTransferActionRunner, useTransferActionScope, useTransferRecordQuery } from './record/useTransferRecord';
import '../service/ExternalServiceTicketDetail.css';
import '../../components/status/StatusChip.css';
import './TransferOrderDetail.css';

/**
 * The transfer record (`/transfers/:transferId/{lines|route|events|chat|history}`), built from the
 * shared record pieces: header and melt bar, one primary lifecycle action, the next-step or R2
 * banner, section tabs (a select on phones) and the summary rail. Sections are static imports.
 * Old section paths (overview, schedule, items, waypoints, shipments, linked, timeline) redirect.
 */
export default function TransferOrderDetail() {
  const params = useParams();
  const location = useLocation();
  const transferId = params.transferId ?? '';
  const resolved = resolveTransferSection(params['*']);
  if (!transferId) {
    return (
      <div className="transfer-record-page transfer-record-page--state">
        <ErrorBanner message="This link has no transfer number." />
        <Link className="btn" to="/transfers">Back to transfers</Link>
      </div>
    );
  }
  if (resolved.redirect) {
    return <Navigate to={`/transfers/${encodeURIComponent(transferId)}/${resolved.section}${location.search}`} replace />;
  }
  return <TransferRecordPage key={transferId} transferId={transferId} section={resolved.section} />;
}

type TransferDialog =
  | { kind: 'scan'; mode: TransferScanMode }
  | { kind: 'ship' }
  | { kind: 'cancel' }
  | { kind: 'dates' }
  | { kind: 'return-leg' }
  | { kind: 'line'; lineId: string | null }
  | { kind: 'stop'; stopId: string | null }
  | { kind: 'shipment'; shipmentId: string | null }
  | { kind: 'link-events' };

const PHONE_QUERY = '(max-width: 640px)';
const PRE_SHIP = new Set(['draft', 'confirmed', 'partially_picked', 'picked']);
const RETURN_LEG_STATUSES = new Set([
  'confirmed', 'partially_picked', 'picked', 'partially_shipped', 'shipped', 'in_transit', 'partially_received', 'received',
]);
const TERMINAL = new Set(['received', 'complete', 'cancelled']);

function initialMatch(query: string): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function BackIcon() {
  return (
    <svg className="event-service-mobile-action__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M10 6 4 12l6 6M4 12h13M17 5h3v14h-3" />
    </svg>
  );
}

function PackingListIcon() {
  return (
    <svg className="transfer-record__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v4h4M10 12h5M10 16h5" />
    </svg>
  );
}

/** The ⋯ menu's items: shipments and stops always; dates, return leg and cancel on Portal transfers. */
function moreMenuItems(record: TransferRecord, actions: TransferActionViews, open: (dialog: TransferDialog) => void): TransferMenuItem[] {
  const { transfer } = record;
  const items: TransferMenuItem[] = [
    { key: 'shipment', label: 'Add shipment', onSelect: () => open({ kind: 'shipment', shipmentId: null }), ...actions.add_shipment },
    { key: 'stop', label: 'Add stop', onSelect: () => open({ kind: 'stop', stopId: null }), ...actions.add_stop },
  ];
  if (isR2Record(record)) return items;
  if (PRE_SHIP.has(transfer.status)) {
    items.push({ key: 'dates', label: 'Change dates', onSelect: () => open({ kind: 'dates' }), ...actions.edit_dates });
  }
  if (transfer.scope === 'temporary' && !transfer.returnLeg && !transfer.parent && RETURN_LEG_STATUSES.has(transfer.status)) {
    items.push({ key: 'return', label: 'Create return leg', onSelect: () => open({ kind: 'return-leg' }), ...actions.create_return });
  }
  if (PRE_SHIP.has(transfer.status)) {
    items.push({ key: 'cancel', label: 'Cancel transfer', onSelect: () => open({ kind: 'cancel' }), danger: true, ...actions.cancel });
  }
  return items;
}

function menuLabel(items: TransferMenuItem[]): string {
  return `More actions: ${items.map((item) => item.label.toLowerCase()).join(', ')}`;
}

/** What Link Events changed, from the links the server says it added or removed. */
function describeLinkChanges(linked: number, unlinked: number): string {
  if (linked && unlinked) return `Linked ${pluralize(linked, 'event')} and unlinked ${pluralize(unlinked, 'event')}`;
  if (linked) return `Linked ${pluralize(linked, 'event')}`;
  if (unlinked) return `Unlinked ${pluralize(unlinked, 'event')}`;
  return 'Nothing changed: the links were already as chosen.';
}

const NO_EVENT_IDS: string[] = [];

function TransferRecordPage({ transferId, section }: { transferId: string; section: TransferSection }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { push } = useToasts();
  const recordQuery = useTransferRecordQuery(transferId);
  const runner = useTransferActionRunner(transferId);
  const record = recordQuery.data ?? null;
  const isPhone = useMediaQuery(PHONE_QUERY, initialMatch(PHONE_QUERY));
  const draftOwner = useSessionStore((state) => state.session?.user?.id ?? null);
  const [dialog, setDialog] = React.useState<TransferDialog | null>(null);
  const [collapsed, setCollapsed] = React.useState(false);
  const recordHeaderRef = React.useRef<HTMLDivElement>(null);
  const headerMelted = useHeaderMelt(recordHeaderRef);
  const [meltSlotNode, setMeltSlotNode] = React.useState<HTMLDivElement | null>(null);

  usePageTitleOverride(record?.transfer.docNo ?? null);

  React.useEffect(() => {
    // The shared Event detail hooks keep record pages responsive the same way.
    document.body.setAttribute('data-event-detail-page', 'true');
    document.body.setAttribute('data-transfer-record-page', 'true');
    return () => {
      document.body.removeAttribute('data-event-detail-page');
      document.body.removeAttribute('data-transfer-record-page');
    };
  }, []);

  const actions = React.useMemo(() => (record ? describeTransferActions(record) : null), [record]);
  const primary = React.useMemo(() => (record ? primaryActionFor(record) : null), [record]);
  const shipShort = React.useMemo(() => (record ? shipShortFor(record) : null), [record]);
  const nextStep = React.useMemo(() => (record ? nextStepFor(record) : null), [record]);
  const tracker = React.useMemo(() => (record ? trackerFor(record) : null), [record]);
  // Only events with a link row can be unlinked; events the lines carry stay while their lines do.
  // Stable between renders, so the picker's ticks survive the page re-rendering under it.
  const linkedEventIds = React.useMemo(
    () => (record ? record.events.filter((event) => event.linked).map((event) => event.id) : NO_EVENT_IDS),
    [record],
  );
  // Each dialog opening is its own session: an unanswered attempt there is retried only from that
  // dialog, and opening a dialog again starts a new action. Keyed by the dialog state object, which
  // changes exactly when a dialog opens, closes or is replaced.
  const scopeRef = React.useRef<{ dialog: TransferDialog | null; scope: string }>({ dialog: null, scope: 'page-dialogs' });
  if (scopeRef.current.dialog !== dialog) {
    scopeRef.current = { dialog, scope: dialog ? newTransferRequestKey() : 'page-dialogs' };
  }
  const dialogRunner = useTransferActionScope(runner, scopeRef.current.scope);

  const openDialog = React.useCallback((next: TransferDialog) => {
    setDialog(next);
  }, []);
  const closeDialog = React.useCallback(() => {
    setDialog(null);
  }, []);
  const finishDialog = React.useCallback((message: string) => {
    setDialog(null);
    push({ type: 'success', message });
  }, [push]);
  const announce = React.useCallback((message: string) => {
    push({ type: 'success', message });
  }, [push]);

  // `?receive=1` (the library's Scan to receive) opens Receive once, when receiving is allowed.
  React.useEffect(() => {
    if (searchParams.get('receive') !== '1' || !record) return;
    const next = new URLSearchParams(searchParams);
    next.delete('receive');
    setSearchParams(next, { replace: true });
    if (!isR2Record(record) && record.actions.receive.allowed) {
      setDialog({ kind: 'scan', mode: { verb: 'Receive', lineIds: null } });
    } else if (actions) {
      push({ type: 'info', message: actions.receive.reason ?? 'Receiving is not open on this transfer.' });
    }
  }, [actions, push, record, searchParams, setSearchParams]);

  const confirm = React.useCallback(async () => {
    try {
      const result = await runner.run(
        'confirm',
        transferId,
        (requestKey) => confirmTransfer(transferId, requestKey),
        { movesStock: true, fallback: 'The transfer was not confirmed.' },
      );
      const leg = result.raw.return_leg;
      const legNo = leg && typeof leg === 'object' && 'doc_no' in leg ? String((leg as { doc_no?: unknown }).doc_no ?? '') : '';
      push({ type: 'success', message: legNo ? `Transfer confirmed. Return leg ${legNo} is ready as a draft.` : 'Transfer confirmed' });
    } catch (error) {
      push({ type: 'error', message: errorText(error, 'The transfer was not confirmed.') });
    }
  }, [push, runner, transferId]);

  const runPrimary = React.useCallback(() => {
    if (!primary || !primary.allowed) return;
    switch (primary.action) {
      case 'confirm':
        void confirm();
        break;
      case 'pick':
        setDialog({ kind: 'scan', mode: { verb: 'Pick' } });
        break;
      case 'ship':
        setDialog({ kind: 'ship' });
        break;
      case 'receive':
        setDialog({ kind: 'scan', mode: { verb: 'Receive', lineIds: null } });
        break;
      default:
        break;
    }
  }, [confirm, primary]);

  const openShipShort = React.useCallback(() => {
    setDialog({ kind: 'ship' });
  }, []);

  const printPackingList = React.useCallback(() => {
    if (!record) return;
    const opened = openPrintableDocument(buildPackingListHtml(packingListInputFor(record)));
    if (!opened) push({ type: 'warn', message: 'Allow pop-ups for this site to print the packing list.' });
  }, [push, record]);

  const handleSectionChange = React.useCallback((key: string) => {
    setCollapsed(false);
    navigate(`/transfers/${encodeURIComponent(transferId)}/${key}`);
  }, [navigate, transferId]);
  const toggleCollapsed = React.useCallback(() => {
    setCollapsed((value) => !value);
  }, []);

  const linkEvents = React.useCallback(async (eventIds: string[]) => {
    if (!record) return;
    // Only link rows change here; an event its lines carry stays listed either way.
    const current = new Set(linkedEventIds);
    const wanted = new Set(eventIds);
    const changes = [
      ...eventIds.filter((id) => !current.has(id)).map((id) => ({ id, linked: true })),
      ...Array.from(current).filter((id) => !wanted.has(id)).map((id) => ({ id, linked: false })),
    ];
    let linked = 0;
    let unlinked = 0;
    for (const change of changes) {
      // EventLinkSelector shows the failure; the links made before it stay made.
      const result = await runner.run(
        `event-link:${change.id}`,
        change,
        (requestKey) => setTransferEventLink(transferId, requestKey, change.id, change.linked),
        { fallback: change.linked ? 'The event was not linked.' : 'The event was not unlinked.' },
      );
      // The server says whether a link row was added or removed (someone may have done it already).
      if (result.raw.changed === true) {
        if (change.linked) linked += 1;
        else unlinked += 1;
      }
    }
    push({ type: linked || unlinked ? 'success' : 'info', message: describeLinkChanges(linked, unlinked) });
  }, [linkedEventIds, push, record, runner, transferId]);

  const unlinkEvent = React.useCallback(async (eventId: string) => {
    const event = record?.events.find((entry) => entry.id === eventId) ?? null;
    const label = event ? eventLabel(event) : 'The event';
    try {
      const result = await runner.run(
        `event-link:${eventId}`,
        { id: eventId, linked: false },
        (requestKey) => setTransferEventLink(transferId, requestKey, eventId, false),
        { fallback: 'The event was not unlinked.' },
      );
      if (result.raw.changed !== true) {
        push({ type: 'info', message: `${label} was no longer linked to this transfer, so nothing changed.` });
      } else if (event?.onLines) {
        push({ type: 'success', message: `Unlinked ${label}. It stays listed because a line on this transfer is for it.` });
      } else {
        push({ type: 'success', message: `Unlinked ${label}` });
      }
    } catch (error) {
      push({ type: 'error', message: errorText(error, 'The event was not unlinked.') });
    }
  }, [push, record, runner, transferId]);

  // A step goes through transfer_save_stop: the same site and R2 rules, request key and History event.
  const stopProgress = React.useCallback(async (stopId: string, status: TransferStopProgress) => {
    try {
      await runner.run(
        `stop-progress:${stopId}`,
        status,
        (requestKey) => setTransferStopProgress(transferId, requestKey, stopId, status),
        { movesStock: status === 'available', fallback: 'The stop was not updated.' },
      );
      push({ type: 'success', message: status === 'shipped' ? 'Stop marked as left' : `Stop marked ${status}` });
    } catch (error) {
      push({ type: 'error', message: errorText(error, 'The stop was not updated.') });
    }
  }, [push, runner, transferId]);

  const refreshRecord = React.useCallback(() => {
    void runner.refresh(true);
  }, [runner]);

  const openAddLine = React.useCallback(() => setDialog({ kind: 'line', lineId: null }), []);
  const openEditLine = React.useCallback((lineId: string) => setDialog({ kind: 'line', lineId }), []);
  const openReceiveLines = React.useCallback((lineIds: string[]) => {
    setDialog({ kind: 'scan', mode: { verb: 'Receive', lineIds } });
  }, []);
  const openAddStop = React.useCallback(() => setDialog({ kind: 'stop', stopId: null }), []);
  const openEditStop = React.useCallback((stopId: string) => setDialog({ kind: 'stop', stopId }), []);
  const openAddShipment = React.useCallback(() => setDialog({ kind: 'shipment', shipmentId: null }), []);
  const openEditShipment = React.useCallback((shipmentId: string) => setDialog({ kind: 'shipment', shipmentId }), []);
  const openLinkEvents = React.useCallback(() => setDialog({ kind: 'link-events' }), []);
  const handleStopProgress = React.useCallback((stopId: string, status: TransferStopProgress) => {
    void stopProgress(stopId, status);
  }, [stopProgress]);
  const handleUnlinkEvent = React.useCallback((eventId: string) => {
    void unlinkEvent(eventId);
  }, [unlinkEvent]);

  if (recordQuery.isLoading) {
    return (
      <div className="transfer-record-page transfer-record-page--state" role="status">
        <p className="transfer-record-empty">Loading transfer…</p>
      </div>
    );
  }
  if (recordQuery.error) {
    return (
      <div className="transfer-record-page transfer-record-page--state">
        <ErrorBanner
          message={readableErrorMessage(recordQuery.error, 'The transfer could not be loaded. Try again.', 'query')}
          onRetry={() => { void recordQuery.refetch(); }}
        />
        <Link className="btn" to="/transfers">Back to transfers</Link>
      </div>
    );
  }
  if (!record || !actions || !tracker) {
    return (
      <div className="transfer-record-page transfer-record-page--state">
        <ErrorBanner message="This transfer was not found, or it is not at one of your sites." />
        <Link className="btn" to="/transfers">Back to transfers</Link>
      </div>
    );
  }

  const { transfer, totals } = record;
  const r2 = isR2Record(record);
  const docNo = transfer.docNo ?? transfer.id.slice(0, 8);
  const description = transfer.description ?? 'Transfer';
  const status = describeStatus('transfer', transfer.status);
  const from = siteName(transfer.fromSite, 'Shipping site');
  const to = siteName(transfer.toSite, 'Receiving site');
  const fromZone = transfer.fromSite?.timezone ?? null;
  const toZone = transfer.toSite?.timezone ?? null;
  const logistics = logisticsSummary(record);
  const shippedAt = formatSiteDateTime(transfer.actualShipAt, fromZone);
  const shipsOn = !shippedAt ? formatSiteDateTime(transfer.expectedShipAt, fromZone) : null;
  const receivedAt = formatSiteDateTime(transfer.actualReceiveAt, toZone);
  const arrivesWord = !receivedAt ? formatSiteDayWord(transfer.expectedReceiveAt, toZone) : null;
  const items = moreMenuItems(record, actions, openDialog);
  const tabs: RecordSectionTab[] = [
    { key: 'lines', label: 'Lines', count: totals.lineCount },
    { key: 'route', label: 'Route & shipments' },
    { key: 'events', label: 'Events', count: record.events.length },
    { key: 'chat', label: 'Chat', count: transfer.chatMessageCount },
    { key: 'history', label: 'History' },
  ];
  const lastSync = r2 ? formatSiteMoment(transfer.lastR2ImportAt, toZone) : null;

  const primaryButton = (className: string, label: string) => (primary ? (
    <TransferActionButton className={className} allowed={primary.allowed} reason={primary.reason} busy={runner.busy} onClick={runPrimary}>
      {label}
    </TransferActionButton>
  ) : null);
  const backLink = (
    <Link className="btn btn-secondary" to="/transfers">Back</Link>
  );
  const packingButton = (
    <button type="button" className="btn btn-secondary" onClick={printPackingList}>Packing list</button>
  );
  const trackerView = (
    <LifecycleStageTracker
      steps={tracker.steps}
      currentStepKey={tracker.currentKey}
      currentStatusLabel={tracker.statusLabel}
      ariaLabel="Transfer status (read only)"
      exceptionLabel={tracker.exceptionLabel}
      busy={runner.busy}
      pulseCurrentStep={!TERMINAL.has(transfer.status)}
      className="transfer-record-tracker"
    />
  );
  const saved = <AutosaveStatus saving={runner.busy} />;

  return (
    <MeltBarSlotProvider isMelted={headerMelted} slotNode={meltSlotNode}>
      <div className="transfer-record-page stack gap-md" data-source={transfer.source}>
        <RecordMeltBar
          melted={headerMelted}
          ariaLabel="Transfer context and actions"
          actionsLabel="Transfer actions"
          moreLabel="More transfer actions"
          title={`${docNo} - ${description}`}
          stage={{ key: transfer.status, label: status.label }}
          meta={`${shortSiteName(transfer.fromSite?.name)} → ${shortSiteName(transfer.toSite?.name)}`}
          primaryActions={(
            <>
              <Link className="btn subtle record-header-melt__navigation-action" to="/transfers" aria-label="Back to transfers">
                <span className="record-header-melt__action-label">Back</span>
              </Link>
              {primaryButton('btn btn-primary record-header-melt__navigation-action', primary?.label ?? '')}
            </>
          )}
          secondaryActions={(
            <>
              <button type="button" className="btn subtle" onClick={printPackingList}>Packing list</button>
              {items.map((item) => (
                <TransferActionButton
                  key={item.key}
                  className={item.danger ? 'btn subtle transfer-record__danger-text' : 'btn subtle'}
                  allowed={item.allowed}
                  reason={item.reason}
                  busy={runner.busy}
                  onClick={item.onSelect}
                >
                  {item.label}
                </TransferActionButton>
              ))}
            </>
          )}
          saveIndicator={saved}
          progress={trackerView}
          slotRef={setMeltSlotNode}
          anchorRef={recordHeaderRef}
        />

        <div ref={recordHeaderRef} className="event-record-header-shell transfer-record-header-shell">
          <RecordHeader
            melted
            title={(
              <div className="record-header__title-block event-record-header__identity transfer-record-header__identity">
                <nav className="record-header__breadcrumb transfer-record-header__crumbs" aria-label="Breadcrumb">
                  <Link to="/transfers">Warehouse</Link>
                  <span aria-hidden="true">›</span>
                  <Link to="/transfers?tab=transfers">Transfers</Link>
                  <span aria-hidden="true">›</span>
                  <span aria-current="page">{docNo}</span>
                </nav>
                <div className="event-record-header__status-row">
                  <span className="record-header__stage" data-stage={transfer.status} data-tone={status.tone}>{status.label}</span>
                  <SourceTag source={transfer.source} />
                  <span className="transfer-record-header__tag">{scopeLabel(transfer)}</span>
                  <LateDate daysLate={transfer.daysLate} />
                </div>
                <h1 className="m0">{docNo} - {description}</h1>
                <div className="event-record-header__description">
                  <span className="transfer-record-header__route">{from} → {to}</span>
                  {shippedAt ? <span>Shipped {shippedAt}</span> : shipsOn ? <span>Ships {shipsOn}</span> : null}
                  {receivedAt ? <span>Received {receivedAt}</span> : arrivesWord ? <span>Arrives {arrivesWord}</span> : null}
                  {logistics ? <b className="transfer-record-header__logistics">{logistics}</b> : null}
                </div>
                <div className="event-record-header__metrics" aria-label="Transfer counts">
                  <span>{pluralize(totals.lineCount, 'line')} · {pluralize(totals.units, 'unit')}</span>
                  {totals.serializedAssets > 0 ? <span>{pluralize(totals.serializedAssets, 'serialized asset')}</span> : null}
                  {totals.shipmentCount > 0 ? <span>{pluralize(totals.shipmentCount, 'shipment')}</span> : null}
                  {transfer.returnLeg ? <span>Return leg {transfer.returnLeg.docNo ?? ''}</span> : null}
                  {transfer.parent ? <span>Return of {transfer.parent.docNo ?? 'a transfer'}</span> : null}
                </div>
              </div>
            )}
            progress={trackerView}
            mobileTitle={<RecordMobileIdentity code={docNo} view="Transfer" name={description} />}
            mobileActions={(
              <div className="event-record-mobile-actions">
                <Link className="btn btn-secondary event-service-mobile-action" to="/transfers" aria-label="Back to transfers" title="Back to transfers">
                  <BackIcon />
                </Link>
                <div className="event-header-mobile-save">{saved}</div>
              </div>
            )}
            mobileNav={(
              <div className="transfer-record-mobile-nav">
                {backLink}
                {packingButton}
                {items.map((item) => (
                  <TransferActionButton
                    key={item.key}
                    className={item.danger ? 'btn btn-secondary transfer-record__danger-text' : 'btn btn-secondary'}
                    allowed={item.allowed}
                    reason={item.reason}
                    busy={runner.busy}
                    onClick={item.onSelect}
                  >
                    {item.label}
                  </TransferActionButton>
                ))}
              </div>
            )}
            aside={(
              <div className="record-header__aside event-record-header__aside">
                <div className="event-record-header__action-row transfer-record-header__actions">
                  {backLink}
                  {primaryButton('btn btn-primary', primary?.label ?? '')}
                  {packingButton}
                  <TransferMoreMenu items={items} label={menuLabel(items)} triggerClassName="btn btn-secondary" busy={runner.busy} />
                </div>
                <div className="event-record-header__saved">{saved}</div>
              </div>
            )}
          />
        </div>

        {r2 ? (
          <R2ManagedBanner reason={R2_TRANSFER_REASON} lastSyncLabel={lastSync} className="transfer-record-banner" />
        ) : nextStep ? (
          <RecordNextStep
            tone={nextStep.tone}
            className="transfer-record-next-step"
            actions={primary || shipShort ? (
              <>
                {primary ? primaryButton('btn sm btn-primary transfer-record-next-step__primary', primary.action === 'receive' ? 'Start receiving' : primary.label) : null}
                {shipShort ? (
                  <TransferActionButton className="btn sm" allowed={shipShort.allowed} reason={shipShort.reason} busy={runner.busy} onClick={openShipShort}>
                    {shipShort.label}
                  </TransferActionButton>
                ) : null}
              </>
            ) : null}
          >
            {nextStep.lead ? <strong>{nextStep.lead}</strong> : null}{nextStep.lead ? ' ' : null}{nextStep.text}
            {primary && !primary.allowed && primary.reason ? (
              <span className="transfer-record-next-step__reason"> {primary.reason}</span>
            ) : null}
          </RecordNextStep>
        ) : null}

        <RecordOverviewLayout className="transfer-record-layout" rail={<TransferSummaryRail record={record} />}>
          <RecordSectionTabs
            tabs={tabs}
            value={section}
            onChange={handleSectionChange}
            ariaLabel="Transfer sections"
            panelId="transfer-record-section"
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
            className="transfer-record-sections"
          >
            {section === 'lines' ? (
              <TransferLinesSection
                record={record}
                actions={actions}
                isPhone={isPhone}
                busy={runner.busy}
                onAddLine={openAddLine}
                onEditLine={openEditLine}
                onReceiveLines={openReceiveLines}
              />
            ) : null}
            {section === 'route' ? (
              <TransferRouteSection
                record={record}
                actions={actions}
                busy={runner.busy}
                onAddStop={openAddStop}
                onEditStop={openEditStop}
                onStopProgress={handleStopProgress}
                onAddShipment={openAddShipment}
                onEditShipment={openEditShipment}
              />
            ) : null}
            {section === 'events' ? (
              <TransferEventsSection
                record={record}
                actions={actions}
                busy={runner.busy}
                onLinkEvents={openLinkEvents}
                onUnlinkEvent={handleUnlinkEvent}
                onRecordChanged={refreshRecord}
              />
            ) : null}
            {section === 'chat' ? <TransferChatSection record={record} /> : null}
            {section === 'history' ? <TransferHistorySection record={record} /> : null}
          </RecordSectionTabs>
        </RecordOverviewLayout>

        <RecordPhoneActionBar
          ariaLabel="Transfer actions"
          primary={primary ? primaryButton('btn btn-primary', primary.phoneLabel) : null}
          secondary={(
            <button type="button" className="btn btn-secondary" onClick={printPackingList} aria-label="Packing list" title="Packing list">
              <PackingListIcon />
            </button>
          )}
        />

        {dialog?.kind === 'scan' ? (
          <TransferScanDialog
            record={record}
            actions={actions}
            mode={dialog.mode}
            runner={dialogRunner}
            draftOwner={draftOwner}
            onClose={closeDialog}
            onApplied={announce}
          />
        ) : null}
        {dialog?.kind === 'ship' ? <TransferShipDialog record={record} runner={dialogRunner} onClose={closeDialog} onDone={finishDialog} /> : null}
        {dialog?.kind === 'cancel' ? <TransferCancelDialog record={record} runner={dialogRunner} onClose={closeDialog} onDone={finishDialog} /> : null}
        {dialog?.kind === 'dates' ? <TransferDatesDialog record={record} runner={dialogRunner} onClose={closeDialog} onDone={finishDialog} /> : null}
        {dialog?.kind === 'return-leg' ? <TransferReturnLegDialog record={record} runner={dialogRunner} onClose={closeDialog} onDone={finishDialog} /> : null}
        {dialog?.kind === 'line' ? (
          <TransferLineDialog
            record={record}
            line={dialog.lineId ? record.lines.find((line) => line.id === dialog.lineId) ?? null : null}
            runner={dialogRunner}
            onClose={closeDialog}
            onDone={finishDialog}
          />
        ) : null}
        {dialog?.kind === 'stop' ? (
          <TransferStopDialog
            record={record}
            stop={dialog.stopId ? record.waypoints.find((stop) => stop.id === dialog.stopId) ?? null : null}
            runner={dialogRunner}
            onClose={closeDialog}
            onDone={finishDialog}
          />
        ) : null}
        {dialog?.kind === 'shipment' ? (
          <TransferShipmentDialog
            record={record}
            shipment={dialog.shipmentId ? record.shipments.find((shipment) => shipment.id === dialog.shipmentId) ?? null : null}
            runner={dialogRunner}
            onClose={closeDialog}
            onDone={finishDialog}
          />
        ) : null}
        {dialog?.kind === 'link-events' ? (
          <EventLinkSelector
            open
            currentEventIds={linkedEventIds}
            onSelect={linkEvents}
            onClose={closeDialog}
          />
        ) : null}
      </div>
    </MeltBarSlotProvider>
  );
}
